import { Api, Collection, DateUtil, FileUtil, ModelRow, StringUtil } from "@mcleod/core";
import { Component } from "../../base/Component";
import { Image } from "../image/Image";
import { Label } from "../label/Label";
import { ReflectiveSnackbars } from "../../base/ReflectiveSnackbars";
import { Table } from "./Table";
import { TableCell } from "./TableCell";
import { TableColumn } from "./TableColumn";
import { TableRow } from "./TableRow";
import { Textbox } from "../textbox/Textbox";

export interface ExportColumn {
    heading: string;
    type: string;
    alignment: string;
}

interface ExportData {
    columns: Collection<ExportColumn>;
    data: Collection<string>[];
    exportFileBase64?: string;
}

export enum ExportType {
    Excel = "XLSX", CSV = "CSV"
}

export class TableExport {
    private table: Table;

    constructor(table: Table) {
        this.table = table;
    }

    async exportTable(showSnackBar = true, type: ExportType = ExportType.Excel): Promise<ExportData> {
        const data: any = await this.getFlattenedTableData();
        data.type = type;
        let snackbar;
        if (showSnackBar === true)
            snackbar = ReflectiveSnackbars.showDownloadSnackbar("Exporting " + this.getListingName(true),
                "Your list is being downloaded.  You can close this and continue working and we'll notify you when it is finished.",
                { persist: true });
        return Api.post("lib/list-export", data).then(response => {
            data.exportFileBase64 = response.data[0].export_data;
            FileUtil.downloadBase64AsFile(data.exportFileBase64, this.getListingName(false) + "." + type.toLowerCase());
            if (snackbar != null)
                snackbar.dismiss();
            ReflectiveSnackbars.showSnackbar("Your list has been exported.  Check the download area of your browser.");
            return data;
        }).catch(reason => {
            if (snackbar != null)
                snackbar.dismiss();
            return data;
        });
    }

    private getListingName(stripTokens: boolean): string {
        let result = this.table.exportName || "Listing Export";
        const tokens = [
            { token: "{timestamp}", replacement: DateUtil.formatDateTime(new Date(), "yyyy-MM-dd HHmm") }
        ];
        for (const token of tokens)
            result = result.replace(token.token, stripTokens ? "" : token.replacement);
        return result.trim();
    }

    private async getFlattenedTableData(): Promise<ExportData> {
        const result = { columns: {}, data: [] };
        for (const row of this.table.filteredRows)
            await this.addFlattenedRowData(result, row);
        return result;
    }

    private async addFlattenedRowData(target: ExportData, row: TableRow): Promise<void> {
        const exportRow = {};
        target.data.push(exportRow);
        for (const column of row.table.columns) {
            const cell: TableCell = await row.createCell(column);
            const children = cell.getRecursiveChildren();
            for (const child of children) {
                this.addExportComponent(exportRow, child, target, column);
            }
        }
    }

    private addExportComponent(exportRow: Collection<any>, comp: Component, target: ExportData, sourceColumn: TableColumn) {
        const fields = comp.getFieldNames();
        const modelRow = comp.getRelevantModelRow();
        if (modelRow == null || (comp instanceof Image && !comp.hasBoundField())) {
            return;
        }
        const exportDetails = comp.exportRowData;
        if (exportDetails != null) {
            exportDetails.forEach((value: any, fieldName: string) => {
                exportRow[fieldName] = value != null ? value.toString() : "";
                if (target.columns[fieldName] == null) {
                    target.columns[fieldName] = this.getColumnDefinition(comp, modelRow, fieldName, sourceColumn);
                }
            });
            return;
        }
        if (fields?.length === 1) {
            this.addExportField(exportRow, fields[0], comp);
            if (target.columns[fields[0]] == null)
                target.columns[fields[0]] = this.getColumnDefinition(comp, modelRow, fields[0], sourceColumn);
        }
        else if (fields != null) {
            for (const field of fields) {
                exportRow[field] = modelRow.get(field, "");
                if (target.columns[field] == null)
                    target.columns[field] = this.getColumnDefinition(comp, modelRow, field, sourceColumn);
            }
        }
    }

    private addExportField(exportRow: Collection<String>, fieldName: string, comp: Component) {
        if (comp instanceof Textbox) {
            if (comp.lookupModelAllowMultiSelect) {
                exportRow[fieldName] = comp.getLookupDisplayValues();
            }
            else {
                exportRow[fieldName] = comp.text;
            }
        }
        else if (comp instanceof Label)
            exportRow[fieldName] = comp.text;
        else {
            const modelRow = comp.getRelevantModelRow();
            exportRow[fieldName] = modelRow.get(fieldName);
        }
        if (exportRow[fieldName] === "hide" || exportRow[comp.field] == null)
            exportRow[fieldName] = "";
    }

    private getColumnDefinition(component: Component, modelRow: ModelRow, fieldName: string, column: TableColumn):
        ExportColumn {
        // Let the component define the export column information for the field, if it knows how to do so
        const exportColumn = component.getExportColumn(fieldName);

        // Default any perts of the export column information that were not provided by the component
        if (StringUtil.isEmptyString(exportColumn.heading)) {
            exportColumn.heading = column.headingCell?.caption;
        }
        if (StringUtil.isEmptyString(exportColumn.heading)) {
            exportColumn.heading = "Column " + column.index;
        }
        if (StringUtil.isEmptyString(exportColumn.type)) {
            const fieldMetadata = modelRow.getMetadata().output[fieldName];
            exportColumn.type = fieldMetadata.dataType ?? "string";
        }
        if (StringUtil.isEmptyString(exportColumn.alignment)) {
            exportColumn.alignment = (column.headingCell.align as unknown) as string;
        }

        return exportColumn;
    }
}
